import React, { useMemo } from 'react'
import clsx from 'classnames'
import moment from 'moment'
import { Helmet } from '@dr.pogodin/react-helmet'
import { useNavigate } from 'react-router'
import { useDispatch, useSelector } from 'react-redux'
import { NewsActionType, type NewsItemPage as NewsItemPageData, type PromotionPage } from 'api/api.types'
import { getSelectedCityId } from 'src/store/app.state.store'
import { initAppointment } from 'src/store/appointment.store'
import { parseHtml } from 'utils/htmlParserWithCustomLinks'
import { AppButton } from 'components/FormElements/AppButton/AppButton'
import { Salons } from 'components/PageElements/Salons/Salons'
import { NewsList } from 'components/PageElements/NewsList/NewsList'
import { PageNotFound } from 'components/PageElements/PageNotFound/PageNotFound'
import { Picture } from 'components/Utility/Media/Picture'
import styles from './NewsItemPage.module.scss'

type NewsItemPageProps = {
  page: NewsItemPageData | PromotionPage,
}

/** One news item or promotion, shown only in the cities it is published for */
export const NewsItemPage: React.FC<NewsItemPageProps> = ({ page }) => {
  const navigate = useNavigate()
  const dispatch = useDispatch()
  const currentCityId = useSelector(getSelectedCityId)
  const vars = page.templateVars?.pageVars
  const date = moment(page.publishedAt)
  const isPromotion = page.template === 'promotion-page'
  const image = vars?.image?.type === 'image' ? vars.image : null

  // The mobile size is the one shared to social networks
  const shareImage = useMemo(
    () => image?.image.find((i) => i.type === 'fullscreen-mobile') ?? null,
    [image],
  )

  if (!vars || currentCityId === null || !vars.cities?.includes(currentCityId)) {
    return <PageNotFound />
  }

  const handleAction = () => {
    const { action } = vars
    if (!action) {
      navigate('#appointment')
    } else if (action.type === NewsActionType.appointment) {
      const service = action.appointment?.service
      dispatch(initAppointment({
        salonId: action.appointment?.salon?.id ?? null,
        services: service ? [{ id: service.id, amount: 1 }] : undefined,
      }))
      navigate('#appointment')
    } else {
      window.open(action.externalLink ?? undefined, '_blank')
    }
  }

  return (<div className={styles.container}>
    <div className={styles.content}>
      <Helmet>
        <meta property={'og:type'} content={'article'} />
        <meta property={'og:article:published_time'} content={date.format('YYYY-MM-DD')} />
        {shareImage && <meta property={'og:image'} content={shareImage.link} />}
        {shareImage && <meta property={'og:image:width'} content={shareImage.resolution.split('x')[0]} />}
        {shareImage && <meta property={'og:image:height'} content={shareImage.resolution.split('x')[1]} />}
      </Helmet>
      <div className={styles.imgBox}>
        <Picture
          className={styles.img}
          image={image?.image}
          desktopType={'fullscreen-desktop'}
          mobileType={'fullscreen-mobile'}
          alt={image?.imageAlt}
        />
      </div>
      <article className={styles.newsContent} itemScope itemType="http://schema.org/NewsArticle">
        <header>
          <time itemProp="datePublished" dateTime={date.format('YYYY-MM-DD')} className={styles.date}>
            {date.format('DD.MM.YYYY')}
          </time>
          <h1 itemProp="name" className={styles.name}>{page.title}</h1>
        </header>
        <div itemProp="description" className={clsx([styles.article, 'text-content'])}>
          {parseHtml(vars.fullText || '')}
        </div>
        <div className={styles.appointmentLink}>
          <AppButton onClick={handleAction}>{vars.actionText || 'Записаться'}</AppButton>
        </div>
      </article>
    </div>
    <div className={styles.newsList}>
      <NewsList perPage={3} noPagination promotions={isPromotion} />
    </div>
    <Salons withCityTitle />
  </div>)
}
